import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card'
import { 
  Copy,
  ExternalLink,
  RefreshCw,
  CheckCircle,
  AlertCircle,
  Loader2
} from 'lucide-react'
import { toast } from 'sonner'
import { api } from '@/lib/api'

interface OpenAIMCPConfigProps {
  serverId: string
  serverName: string
}

interface OpenAIMCPConfigData {
  server_id: string
  server_name: string
  openai_format: {
    type: 'mcp'
    server_label: string
    server_url: string
    headers?: Record<string, string>
    allowed_tools?: string[]
    require_approval?: 'never' | 'always'
    client_timeout_ms?: number
    use_cache_of_tools_list?: boolean
  }
  generated_at: string
  message: string
}

export function OpenAIMCPConfig({ serverId, serverName }: OpenAIMCPConfigProps) {
  const [mcpConfig, setMcpConfig] = useState<OpenAIMCPConfigData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [refreshing, setRefreshing] = useState(false)

  const fetchMCPConfig = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await api.get(`/servers/${serverId}/mcp-config`) as OpenAIMCPConfigData
      setMcpConfig(response)
    } catch (err: any) {
      setError(err.message || 'Failed to fetch MCP configuration')
      console.error('Error fetching MCP config:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleRefresh = async () => {
    try {
      setRefreshing(true)
      await fetchMCPConfig()
      toast.success('MCP configuration refreshed')
    } catch (err) {
      toast.error('Failed to refresh MCP configuration')
    } finally {
      setRefreshing(false)
    }
  }

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text)
      toast.success('Copied to clipboard')
    } catch (err) {
      toast.error('Failed to copy to clipboard')
    }
  }

  const copyFullConfig = async () => {
    if (!mcpConfig) return
    
    const configText = JSON.stringify(mcpConfig.openai_format, null, 2)
    await copyToClipboard(configText)
  }

  useEffect(() => {
    fetchMCPConfig()
  }, [serverId])

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <div className="w-6 h-6 rounded-full bg-gradient-to-r from-green-400 to-blue-500 flex items-center justify-center">
              <span className="text-white text-xs font-bold">AI</span>
            </div>
            <span>OpenAI MCP Configuration</span>
          </CardTitle>
          <CardDescription>
            Live OpenAI MCP configuration for this server
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin" />
            <span className="ml-2">Loading MCP configuration...</span>
          </div>
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <div className="w-6 h-6 rounded-full bg-gradient-to-r from-green-400 to-blue-500 flex items-center justify-center">
              <span className="text-white text-xs font-bold">AI</span>
            </div>
            <span>OpenAI MCP Configuration</span>
          </CardTitle>
          <CardDescription>
            Live OpenAI MCP configuration for this server
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8 text-red-500">
            <AlertCircle className="h-6 w-6" />
            <span className="ml-2">{error}</span>
          </div>
          <div className="flex justify-center">
            <Button variant="outline" onClick={() => fetchMCPConfig()}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Retry
            </Button>
          </div>
        </CardContent>
      </Card>
    )
  }

  if (!mcpConfig) {
    return null
  }

  const { openai_format } = mcpConfig
  const hasAuth = openai_format.require_approval === 'always'
  const hasHeaders = openai_format.headers && Object.keys(openai_format.headers).length > 0

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <div className="w-6 h-6 rounded-full bg-gradient-to-r from-green-400 to-blue-500 flex items-center justify-center">
              <span className="text-white text-xs font-bold">AI</span>
            </div>
            <span>OpenAI MCP Configuration</span>
          </div>
          <div className="flex items-center space-x-2">
            <Button
              size="sm"
              variant="outline"
              onClick={handleRefresh}
              disabled={refreshing}
            >
              <RefreshCw className={`mr-2 h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              size="sm"
              onClick={copyFullConfig}
            >
              <Copy className="mr-2 h-4 w-4" />
              Copy Config
            </Button>
          </div>
        </CardTitle>
        <CardDescription>
          Live OpenAI MCP configuration that automatically reflects current server authentication state
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Status Overview */}
        <div className="grid gap-4 md:grid-cols-3">
          <div className="flex items-center space-x-2 p-3 bg-gray-50 rounded-lg">
            <CheckCircle className={`h-5 w-5 ${hasAuth ? 'text-yellow-500' : 'text-green-500'}`} />
            <div>
              <div className="font-medium text-sm">Approval Required</div>
              <div className="text-xs text-gray-600">
                {openai_format.require_approval || 'never'}
              </div>
            </div>
          </div>
          
          <div className="flex items-center space-x-2 p-3 bg-gray-50 rounded-lg">
            <CheckCircle className={`h-5 w-5 ${hasHeaders ? 'text-green-500' : 'text-gray-400'}`} />
            <div>
              <div className="font-medium text-sm">Headers</div>
              <div className="text-xs text-gray-600">
                {hasHeaders ? `${Object.keys(openai_format.headers!).length} headers` : 'None'}
              </div>
            </div>
          </div>
          
          <div className="flex items-center space-x-2 p-3 bg-gray-50 rounded-lg">
            <CheckCircle className={`h-5 w-5 ${openai_format.allowed_tools ? 'text-blue-500' : 'text-gray-400'}`} />
            <div>
              <div className="font-medium text-sm">Allowed Tools</div>
              <div className="text-xs text-gray-600">
                {openai_format.allowed_tools ? `${openai_format.allowed_tools.length} tools` : 'All tools'}
              </div>
            </div>
          </div>
        </div>

        {/* Quick Copy Section */}
        <div className="space-y-3">
          <h4 className="font-medium">Quick Copy</h4>
          <div className="grid gap-2">
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600 w-24">Server Label:</span>
              <code className="text-sm bg-gray-100 px-2 py-1 rounded flex-1">
                {openai_format.server_label}
              </code>
              <Button
                size="sm"
                variant="outline"
                onClick={() => copyToClipboard(openai_format.server_label)}
              >
                <Copy className="h-3 w-3" />
              </Button>
            </div>
            
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600 w-24">Server URL:</span>
              <code className="text-sm bg-gray-100 px-2 py-1 rounded flex-1">
                {openai_format.server_url}
              </code>
              <Button
                size="sm"
                variant="outline"
                onClick={() => copyToClipboard(openai_format.server_url)}
              >
                <Copy className="h-3 w-3" />
              </Button>
            </div>
          </div>
        </div>

        {/* Authentication Headers */}
        {hasHeaders && (
          <div className="space-y-3">
            <h4 className="font-medium">Authentication Headers</h4>
            <div className="space-y-2">
              {Object.entries(openai_format.headers!).map(([header, value]) => (
                <div key={header} className="flex items-center space-x-2">
                  <span className="text-sm text-gray-600 w-24">{header}:</span>
                  <code className="text-sm bg-gray-100 px-2 py-1 rounded flex-1">
                    {value}
                  </code>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => copyToClipboard(`${header}: ${value}`)}
                  >
                    <Copy className="h-3 w-3" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Additional Config Fields */}
        {(openai_format.allowed_tools || openai_format.client_timeout_ms || openai_format.use_cache_of_tools_list) && (
          <div className="space-y-3">
            <h4 className="font-medium">Additional Configuration</h4>
            <div className="bg-gray-50 p-4 rounded-lg space-y-2">
              {openai_format.allowed_tools && (
                <div>
                  <span className="text-sm font-medium text-gray-700">Allowed Tools:</span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {openai_format.allowed_tools.map((tool) => (
                      <Badge key={tool} variant="secondary" className="text-xs">{tool}</Badge>
                    ))}
                  </div>
                </div>
              )}
              {openai_format.client_timeout_ms && (
                <div>
                  <span className="text-sm font-medium text-gray-700">Client Timeout:</span>
                  <span className="text-sm text-gray-600 ml-2">{openai_format.client_timeout_ms}ms</span>
                </div>
              )}
              {openai_format.use_cache_of_tools_list && (
                <div>
                  <span className="text-sm font-medium text-gray-700">Tools List Caching:</span>
                  <Badge variant="outline" className="ml-2">Enabled</Badge>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Full Configuration */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="font-medium">Complete OpenAI Configuration</h4>
            <div className="flex items-center space-x-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => window.open('https://platform.openai.com/docs/guides/model-configuration', '_blank')}
              >
                <ExternalLink className="mr-2 h-3 w-3" />
                OpenAI Docs
              </Button>
            </div>
          </div>
          <div className="relative">
            <pre className="text-xs bg-gray-50 p-4 rounded-lg overflow-x-auto max-h-96 border">
              {JSON.stringify(openai_format, null, 2)}
            </pre>
            <Button
              size="sm"
              className="absolute top-2 right-2"
              onClick={copyFullConfig}
            >
              <Copy className="h-3 w-3" />
            </Button>
          </div>
        </div>

        {/* Last Generated */}
        <div className="text-xs text-gray-500 border-t pt-3">
          <div className="flex items-center justify-between">
            <span>Generated: {new Date(mcpConfig.generated_at).toLocaleString()}</span>
            <span className="flex items-center space-x-1">
              <div className="w-2 h-2 rounded-full bg-green-400"></div>
              <span>Live configuration</span>
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  )
} 